import { useMemo, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { createAccountAdminInvitation } from "../../pages/Directory/api";

/**
 * NewAccountWizard — SCK-621 (Layer 2b), ported into the console.
 *
 * SYSTEM-only bootstrap wizard for provisioning a brand-new account +
 * minting its first ACCOUNT_ADMIN invitation in a single flow. Wraps
 * the SCK-620 backend endpoint POST /api/v1/invitations/account-admin.
 *
 * Kept as a self-contained modal so DirectoryAccounts (and future
 * call sites) only need `<NewAccountWizard open onClose onCreated />`.
 *
 * Adds `beta` boolean over the admin-app port: checking the box hides
 * the Billing block and sends `beta:true` in the payload so coord can
 * skip the trial-clock enforcement + billing-required nudges for
 * design-partner accounts.
 */

const STEPS = ["Account details", "First ACCOUNT_ADMIN invite"];

const DEFAULT_MAX_PROJECTS = 10;
const DEFAULT_TRIAL_LENGTH_DAYS = 30;

const MIN_ACCOUNT_NAME = 3;
const MAX_PROJECTS_UPPER = 100;
const TRIAL_DAYS_UPPER = 365;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewAccountWizard({ open, onClose, onCreated }) {
  // ---- stepper ----
  const [activeStep, setActiveStep] = useState(0);

  // ---- step 1 fields ----
  const [accountName, setAccountName] = useState("");
  const [beta, setBeta] = useState(false);
  const [maxProjects, setMaxProjects] = useState(String(DEFAULT_MAX_PROJECTS));
  const [trialLengthDays, setTrialLengthDays] = useState(
    String(DEFAULT_TRIAL_LENGTH_DAYS)
  );
  const [billingExpanded, setBillingExpanded] = useState(false);
  const [billingStripeCustomerId, setBillingStripeCustomerId] = useState("");
  const [billingContactEmail, setBillingContactEmail] = useState("");
  const [billingNotes, setBillingNotes] = useState("");

  // ---- step 2 fields ----
  const [adminEmail, setAdminEmail] = useState("");
  const [deliveryMode, setDeliveryMode] = useState("email");
  const [notes, setNotes] = useState("");

  // ---- feedback ----
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // A beta account skips billing entirely — trial expiry is not
  // enforced on the server, and any billing block on the payload
  // would just be inert noise.
  const billingInfoPayload = useMemo(() => {
    if (beta) return undefined;
    const stripe = billingStripeCustomerId.trim();
    const contact = billingContactEmail.trim();
    const bn = billingNotes.trim();
    if (!stripe && !contact && !bn) return undefined;
    return {
      stripeCustomerId: stripe || undefined,
      contactEmail: contact || undefined,
      notes: bn || undefined,
    };
  }, [beta, billingStripeCustomerId, billingContactEmail, billingNotes]);

  const resetAll = () => {
    setActiveStep(0);
    setAccountName("");
    setBeta(false);
    setMaxProjects(String(DEFAULT_MAX_PROJECTS));
    setTrialLengthDays(String(DEFAULT_TRIAL_LENGTH_DAYS));
    setBillingExpanded(false);
    setBillingStripeCustomerId("");
    setBillingContactEmail("");
    setBillingNotes("");
    setAdminEmail("");
    setDeliveryMode("email");
    setNotes("");
    setFieldErrors({});
    setFormError(null);
    setResult(null);
    setCopied(false);
  };

  const handleClose = () => {
    if (submitting) return;
    resetAll();
    onClose();
  };

  const handleDone = () => {
    const r = result;
    resetAll();
    onClose();
    if (r) onCreated(r);
  };

  // ---- validation ------------------------------------------------------

  const validateStep1 = () => {
    const errs = {};
    const trimmed = accountName.trim();
    if (trimmed.length < MIN_ACCOUNT_NAME) {
      errs.accountName = `Account name must be at least ${MIN_ACCOUNT_NAME} characters.`;
    }
    const mp = parseInt(maxProjects, 10);
    if (Number.isNaN(mp) || mp < 1 || mp > MAX_PROJECTS_UPPER) {
      errs.maxProjects = `Must be a whole number between 1 and ${MAX_PROJECTS_UPPER}.`;
    }
    const td = parseInt(trialLengthDays, 10);
    if (Number.isNaN(td) || td < 1 || td > TRIAL_DAYS_UPPER) {
      errs.trialLengthDays = `Must be a whole number between 1 and ${TRIAL_DAYS_UPPER}.`;
    }
    if (!beta) {
      const bcEmail = billingContactEmail.trim();
      if (bcEmail && !EMAIL_RE.test(bcEmail)) {
        errs.billingInfo = "Billing contact email is not a valid email address.";
      }
    }
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    const trimmed = adminEmail.trim();
    if (!trimmed) {
      errs.email = "Admin email is required.";
    } else if (!EMAIL_RE.test(trimmed)) {
      errs.email = "Enter a valid email address.";
    }
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setFormError(null);
    setActiveStep(1);
  };

  const handleBack = () => {
    setFieldErrors({});
    setFormError(null);
    setActiveStep(0);
  };

  // ---- submit ----------------------------------------------------------

  const handleSubmit = async () => {
    const errs = validateStep2();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setFormError(null);
    setSubmitting(true);

    try {
      const res = await createAccountAdminInvitation({
        email: adminEmail.trim(),
        accountName: accountName.trim(),
        beta,
        trialLengthDays: parseInt(trialLengthDays, 10),
        maxProjects: parseInt(maxProjects, 10),
        deliveryMode,
        billingInfo: billingInfoPayload,
        notes: notes.trim() || undefined,
      });
      setResult(res);
    } catch (err) {
      if (err.status === 400) {
        let body = null;
        try {
          body = err.body ? JSON.parse(err.body) : null;
        } catch {
          body = null;
        }
        const serverFields = body?.fieldErrors || {};
        const mapped = {};
        for (const [k, v] of Object.entries(serverFields)) {
          if (
            k === "accountName" ||
            k === "maxProjects" ||
            k === "trialLengthDays" ||
            k === "email" ||
            k === "billingInfo" ||
            k === "deliveryMode"
          ) {
            mapped[k] = v;
          }
        }
        if (Object.keys(mapped).length) {
          setFieldErrors(mapped);
          if (
            mapped.accountName ||
            mapped.maxProjects ||
            mapped.trialLengthDays ||
            mapped.billingInfo
          ) {
            setActiveStep(0);
          }
        } else {
          setFormError(body?.message || err.message || "Invalid request.");
        }
      } else if (err.status === 403) {
        setFormError("Only SYSTEM users can bootstrap accounts.");
      } else if (err.status >= 500) {
        setFormError(
          `The server hit an error (${err.status}). Try again in a moment; if the problem persists, capture the details and ping platform.`
        );
      } else {
        setFormError(err.message || "Failed to create account.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setFormError(null);
    void handleSubmit();
  };

  const copyRedemptionUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — silent
    }
  };

  // ---- render ----------------------------------------------------------

  const handleCreateAnother = () => {
    // Preserve the parent's onCreated callback by firing it first, then
    // reset only this wizard's state so the caller can keep the dialog
    // open for another bootstrap without a full unmount cycle.
    if (result) onCreated(result);
    resetAll();
  };

  if (result) {
    const inv = result.invitation || {};
    const expiresIso = inv.expires_on ?? inv.expiresOn ?? null;
    const shouldShowUrl =
      inv.deliveryMode === "copy_only" ||
      deliveryMode === "copy_only" ||
      inv.sesEnabled === false;
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CheckCircleOutlineIcon color="success" />
            <span>Account created</span>
          </Stack>
          <IconButton size="small" onClick={handleDone} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Account <strong>{inv.accountName || accountName}</strong>{" "}
            bootstrapped. Invitation sent to{" "}
            <strong>{inv.email || adminEmail}</strong>.
          </Typography>

          {shouldShowUrl && inv.redemptionUrl && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Copy this redemption link and send it manually — the token is
                shown once and cannot be retrieved again.
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={inv.redemptionUrl}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => copyRedemptionUrl(inv.redemptionUrl)}
                      title="Copy link"
                      aria-label="Copy redemption link"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
              {copied && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ display: "block", mt: 1 }}
                >
                  Copied to clipboard.
                </Typography>
              )}
            </Box>
          )}

          {expiresIso && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2 }}
            >
              Invitation expires {new Date(expiresIso).toLocaleString()}.
            </Typography>
          )}
          {inv.billingInfoCaptured && !beta && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Billing info captured — account will start on BILLING once the
              invitation is redeemed.
            </Typography>
          )}
          {beta && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Beta account — trial-clock enforcement is skipped.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateAnother}>Create another</Button>
          <Button variant="contained" onClick={handleDone}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        New account
        <IconButton size="small" onClick={handleClose} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ position: "relative" }}>
        <Backdrop
          open={submitting}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.5)"
                : "rgba(255,255,255,0.7)",
            display: "flex",
            gap: 2,
            flexDirection: "column",
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Creating account…
          </Typography>
        </Backdrop>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            "& .MuiStepIcon-root": { fontSize: 28 },
          }}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Provision a new account. The first ACCOUNT_ADMIN is invited in
              the next step.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Account details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Account name"
                  size="small"
                  required
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  error={!!fieldErrors.accountName}
                  helperText={
                    fieldErrors.accountName ||
                    `At least ${MIN_ACCOUNT_NAME} characters.`
                  }
                  inputProps={{ "aria-label": "Account name" }}
                />
                <Tooltip
                  title="Beta accounts get the beta feature-flag set and skip billing setup. Convert to a regular account later from the account detail page."
                  arrow
                  enterDelay={400}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={beta}
                        onChange={(e) => setBeta(e.target.checked)}
                      />
                    }
                    label="Beta account (skip billing + trial-clock enforcement)"
                  />
                </Tooltip>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Max projects"
                    size="small"
                    type="number"
                    fullWidth
                    required
                    value={maxProjects}
                    onChange={(e) => setMaxProjects(e.target.value)}
                    error={!!fieldErrors.maxProjects}
                    helperText={
                      fieldErrors.maxProjects ||
                      `Default ${DEFAULT_MAX_PROJECTS}. 1–${MAX_PROJECTS_UPPER}.`
                    }
                    inputProps={{
                      min: 1,
                      max: MAX_PROJECTS_UPPER,
                      "aria-label": "Max projects",
                    }}
                  />
                  <TextField
                    label="Trial length (days)"
                    size="small"
                    type="number"
                    fullWidth
                    required
                    value={trialLengthDays}
                    onChange={(e) => setTrialLengthDays(e.target.value)}
                    error={!!fieldErrors.trialLengthDays}
                    helperText={
                      fieldErrors.trialLengthDays ||
                      `Default ${DEFAULT_TRIAL_LENGTH_DAYS}. 1–${TRIAL_DAYS_UPPER}.`
                    }
                    inputProps={{
                      min: 1,
                      max: TRIAL_DAYS_UPPER,
                      "aria-label": "Trial length in days",
                    }}
                    disabled={beta}
                  />
                </Stack>
              </Stack>
            </Paper>

            {!beta && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => setBillingExpanded((v) => !v)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                  aria-expanded={billingExpanded}
                  aria-controls="billing-info-block"
                >
                  {billingExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    component="span"
                  >
                    Billing info (optional)
                  </Typography>
                </Link>
                <Collapse in={billingExpanded} unmountOnExit id="billing-info-block">
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Any field below marks the invitation as billing-prefilled.
                      Leave the whole block empty to keep the account on trial.
                    </Typography>
                    <TextField
                      label="Stripe customer ID"
                      size="small"
                      value={billingStripeCustomerId}
                      onChange={(e) => setBillingStripeCustomerId(e.target.value)}
                      placeholder="cus_..."
                    />
                    <TextField
                      label="Billing contact email"
                      size="small"
                      type="email"
                      value={billingContactEmail}
                      onChange={(e) => setBillingContactEmail(e.target.value)}
                      error={!!fieldErrors.billingInfo}
                      helperText={fieldErrors.billingInfo || " "}
                    />
                    <TextField
                      label="Notes"
                      size="small"
                      multiline
                      minRows={2}
                      value={billingNotes}
                      onChange={(e) => setBillingNotes(e.target.value)}
                      placeholder="Freeform — invoicing terms, cost-center, contract link, etc."
                    />
                  </Stack>
                </Collapse>
              </Paper>
            )}

            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Invite the first ACCOUNT_ADMIN for{" "}
              <strong>{accountName.trim()}</strong>.
            </Typography>

            <TextField
              label="Admin email"
              size="small"
              required
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              error={!!fieldErrors.email}
              helperText={
                fieldErrors.email || "They will receive the redemption link."
              }
              inputProps={{ "aria-label": "Admin email" }}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Delivery
              </Typography>
              <RadioGroup
                row
                value={deliveryMode}
                onChange={(e) => setDeliveryMode(e.target.value)}
                aria-label="Delivery mode"
              >
                <Tooltip
                  title="Send the redemption link via email (requires SES enabled)."
                  arrow
                  enterDelay={400}
                >
                  <FormControlLabel
                    value="email"
                    control={<Radio size="small" />}
                    label="Email the invitee"
                  />
                </Tooltip>
                <Tooltip
                  title="Just show the URL — share it manually via your own channel."
                  arrow
                  enterDelay={400}
                >
                  <FormControlLabel
                    value="copy_only"
                    control={<Radio size="small" />}
                    label="Copy link only (no email)"
                  />
                </Tooltip>
              </RadioGroup>
            </Box>

            <TextField
              label="Operator notes"
              size="small"
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — internal context for who this is and why."
            />

            {/* Summary */}
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Summary
              </Typography>
            </Divider>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                bgcolor: "action.hover",
                fontSize: 13,
              }}
            >
              <Stack spacing={0.5}>
                <div>
                  <strong>Account:</strong> {accountName.trim() || "—"}
                  {beta && " (beta)"}
                </div>
                <div>
                  <strong>Admin:</strong> {adminEmail.trim() || "—"}
                </div>
                <div>
                  <strong>Max projects:</strong> {maxProjects}
                </div>
                <div>
                  <strong>Trial length:</strong>{" "}
                  {beta ? "n/a (beta)" : `${trialLengthDays} days`}
                </div>
                <div>
                  <strong>Delivery:</strong>{" "}
                  {deliveryMode === "email"
                    ? "Email the invitee"
                    : "Copy link only"}
                </div>
                {billingInfoPayload && (
                  <div>
                    <strong>Billing:</strong> prefilled
                  </div>
                )}
              </Stack>
            </Box>

            {formError && (
              <Alert
                severity="error"
                action={
                  formError.startsWith("The server hit an error") ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRetry}
                      disabled={submitting}
                    >
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {formError}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {activeStep === 1 && (
          <Button onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        {activeStep === 0 && (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {submitting ? "Creating…" : "Create invite"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
